import { useCallback } from 'react';
import { safeStr } from './applicationDetailShared.js';
import './applications.css';

// ---------------------------------------------------------------------------
// Title-case helper — converts kebab/underscore names to display labels.
// "scm-adapter" → "SCM Adapter", "plaid-tool-adapter" → "Plaid Tool Adapter".
// Short tokens (≤ 3 chars) are uppercased as likely acronyms (SCM, MCP, etc.).
// Used as a fallback when providerDisplayName is absent on the wire.
// ---------------------------------------------------------------------------
function titleCaseProviderRef(ref) {
  if (!ref || typeof ref !== 'string') return '';
  return ref
    .split(/[-_]+/)
    .filter(Boolean)
    .map((token) => {
      if (token.length <= 3) return token.toUpperCase();
      return token.charAt(0).toUpperCase() + token.slice(1);
    })
    .join(' ');
}

// ---------------------------------------------------------------------------
// PhasePill — coloured dot + status text.
// Available = green, Degraded = amber, Disabled = grey.
// Omitted entirely when phase is absent or unrecognised.
// ---------------------------------------------------------------------------
const PHASE_MAP = {
  available: 'available',
  degraded:  'degraded',
  disabled:  'disabled',
};

function PhasePill({ phase }) {
  if (!phase || typeof phase !== 'string') return null;
  const key = phase.toLowerCase();
  const cls = PHASE_MAP[key];
  if (!cls) return null;
  return (
    <span className={`phase-pill ${cls}`}>
      <span className="phase-dot" />
      {phase}
    </span>
  );
}

// ---------------------------------------------------------------------------
// installRequestYAML — MVP stub for the admin section.
// ---------------------------------------------------------------------------
function installRequestYAML(app, requestedBy) {
  // MVP stub: a plausible-looking YAML for humans to paste into a PR.
  // The operator doesn't consume this today (JOBE-0025.5 explicitly
  // stubs the install flow). requestedBy comes from the install
  // fixture when present; falls back to a <pending> sentinel that's
  // obviously-a-placeholder in the pasted YAML.
  return [
    '# jobe install request (draft)',
    `apiVersion: platform.jobe.dev/v1alpha1`,
    `kind: ApplicationInstall`,
    `metadata:`,
    `  name: ${app.name}`,
    `spec:`,
    `  applicationRef: ${app.name}`,
    `  requestedBy: ${requestedBy || '<pending>'}`,
    `  provider:`,
    `    crRef: ${app.integrations?.[0]?.providerRef || ''}`,
    '',
  ].join('\n');
}

// ---------------------------------------------------------------------------
// formatRelativeTime — turn an ISO-8601 timestamp into "3 minutes ago",
// "2 hours ago", "5 days ago". Returns the empty string for missing
// timestamps. Used by the Activity strip only — kept inline rather than
// pulled into a shared lib because no other surface needs it yet.
//
// Coarse buckets (seconds / minutes / hours / days / weeks) are good
// enough for an at-a-glance activity signal; the OverviewTab caller
// gates rendering on the timestamp being non-null, so we never get a
// "0 seconds ago" flash.
// ---------------------------------------------------------------------------
function formatRelativeTime(iso) {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const deltaSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const deltaMin = Math.floor(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin}m ago`;
  const deltaHr = Math.floor(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr}h ago`;
  const deltaDay = Math.floor(deltaHr / 24);
  if (deltaDay < 7) return `${deltaDay}d ago`;
  const deltaWk = Math.floor(deltaDay / 7);
  return `${deltaWk}w ago`;
}

// ---------------------------------------------------------------------------
// OverviewTab — Board 04 V1 (refined incremental).
//
// Changes vs pre-JOBE-0148.3:
//   1. PhasePill in hero upper-right (green/amber/grey dot + status text).
//   2. Full URN (urn:jobe:application:{name}) in mono, not the truncated form.
//   3. Launch button ("Open ↗") rendered only when app.launchURL is non-empty.
//   4. Integration row shows title-cased displayName + faint mono providerRef tail.
//      Per-provider phase pill when provider phase differs from app phase (guarded).
//   5. Owner group as .meta-label / .meta-value key-value row, not italic chrome.
//   6. "Integrations · N" header (was "N total").
//   7. Activity section: JOBE-0150 — wired to /api/applications/:name/activity.
//      Renders only when activity.lastInvocation is non-null; cold or
//      never-invoked Applications skip the section entirely so the
//      Overview stays quiet.
// ---------------------------------------------------------------------------
export function OverviewTab({ app, activity, onToast }) {
  const isInstallRequest = app.installRequest && app.installRequest.configured === false;

  // Full URN: wire exposes app.name; construct the canonical form.
  const fullURN = `urn:jobe:application:${app.name}`;

  // Phase from wire (top-level string: "Available" | "Degraded" | "Disabled").
  const appPhase = app.phase || null;

  const integrations = app.integrations || [];

  // handleInstallCopy kept from original for the admin section.
  const handleInstallCopy = useCallback(async () => {
    const yaml = installRequestYAML(app, app.installRequest?.requestedBy);
    try {
      await navigator.clipboard.writeText(yaml);
      onToast('PR flow not wired yet; YAML copied to clipboard.');
    } catch {
      onToast('PR flow not wired yet; clipboard write failed.');
    }
  }, [app, onToast]);

  return (
    <>
      {/* ------------------------------------------------------------------ */}
      {/* Hero card: icon | name/URN/description block | phase pill           */}
      {/* ------------------------------------------------------------------ */}
      <div className="hero hero-v2">
        <div className="icon">{app.name?.[0]?.toUpperCase() || '?'}</div>
        <div className="ht ht-flex">
          <div className="ht-main">
            <h3>{safeStr(app.displayName) || safeStr(app.name)}</h3>
            {/* Full URN — informational, no copy affordance in v1 */}
            <div className="urn urn-full">{fullURN}</div>
            {/* Launch button — rendered only when launchURL is non-empty */}
            {app.launchURL && (
              <a
                href={app.launchURL}
                target="_blank"
                rel="noopener noreferrer"
                className="launch-btn"
              >
                Open ↗
              </a>
            )}
            {safeStr(app.description) && <p className="lede">{app.description}</p>}
          </div>
          {/* Phase pill anchored to top-right of the hero flex row */}
          <div className="hero-pill-anchor">
            <PhasePill phase={appPhase} />
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Owner group — labelled metadata row (not italic title chrome)       */}
      {/* ------------------------------------------------------------------ */}
      {safeStr(app.ownerGroup?.name) && (
        <div className="sec meta-section">
          <div className="meta-row">
            <span className="meta-label">Owner group</span>
            <span className="meta-value">{safeStr(app.ownerGroup.title) || safeStr(app.ownerGroup.name)}</span>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Integrations section — "Integrations · N" header                   */}
      {/* ------------------------------------------------------------------ */}
      <div className="sec">
        <h4>Integrations · {integrations.length}</h4>
        <div className="faces">
          {integrations.length === 0 ? (
            <div style={{ fontFamily: 'var(--ff-mono)', color: 'var(--ink-soft)', fontSize: 12 }}>no integrations</div>
          ) : integrations.map((integ, i) => {
            // Primary label: providerDisplayName from wire (JOBE-0149 + PR #972).
            // Falls back to title-cased providerRef for dangling refs where the
            // Provider CR was not resolved (no providerDisplayName on wire).
            const displayLabel = safeStr(integ.providerDisplayName)
              || titleCaseProviderRef(integ.providerRef)
              || safeStr(integ.type)
              || '—';
            return (
              <div key={`${integ.type}:${integ.providerRef || i}`} className="face integration-face">
                <div className="fh integration-fh">
                  <span className="integration-name">{displayLabel}</span>
                </div>
                {safeStr(integ.providerRef) && (
                  <div className="integration-ref">{integ.providerRef}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Entity types                                                         */}
      {/* ------------------------------------------------------------------ */}
      {(app.entityTypes || []).length > 0 && (
        <div className="sec">
          <h4>Entity types</h4>
          <div className="entRow">
            {app.entityTypes.filter((e) => typeof e === 'string').map((e) => <span key={e} className="epill">{e}</span>)}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Activity strip — JOBE-0150.                                         */}
      {/* Renders only when the API returned a non-null lastInvocation.       */}
      {/* Cold / never-invoked / fetch-failed all skip the section so the     */}
      {/* Overview stays quiet for unused Applications.                       */}
      {/* ------------------------------------------------------------------ */}
      {activity?.lastInvocation && (
        <div className="sec meta-section">
          <div className="meta-row">
            <span className="meta-label">Activity</span>
            <span className="meta-value">
              Last invoked {formatRelativeTime(activity.lastInvocation)}
              {' · '}
              {activity.windows?.last24h?.invocations ?? 0} in 24h
              {' · '}
              {activity.windows?.last7d?.invocations ?? 0} in 7d
            </span>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Admin / install-request strip (unchanged from pre-0148.3)           */}
      {/* ------------------------------------------------------------------ */}
      {isInstallRequest ? (
        <div className="sec">
          <h4>Install request</h4>
          <div className="admin">
            <div className="rows">
              <div className="r">
                <div className="t">
                  Not yet wired for this workspace.
                  <span className="sub">Opening the install PR creates a YAML stub you paste into jobe-infra.</span>
                </div>
                <button className="go" onClick={handleInstallCopy}>Open install PR →</button>
              </div>
            </div>
          </div>
        </div>
      ) : app.admin ? (
        <div className="sec">
          <h4>
            Admin
            <span className="lock">owner · {safeStr(app.ownerGroup?.name) || 'unknown'}</span>
          </h4>
          <div className="admin">
            <div className="rows">
              <div className="r">
                <div className="t">
                  Rotate credentials
                  <span className="sub">replaces the stored secret bundle</span>
                </div>
                <button className="go" disabled title="not wired yet" onClick={(e) => e.preventDefault()}>
                  Rotate
                </button>
              </div>
              <div className="r">
                <div className="t">
                  Enable / disable
                  <span className="sub">pause or resume this provider</span>
                </div>
                <button className="go" disabled title="not wired yet" onClick={(e) => e.preventDefault()}>
                  Toggle
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
